import React, { useCallback, useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { enablePush, isPushSupported, pushPermission, isIOS, isStandalone } from '@/lib/push';

// First-sign-in notification enablement for the installed Coach OS PWA — the SAME proven flow Client OS
// uses (lib/push + /push/status + /push/subscribe + /push/test), but writing a COACH subscription.
// Also self-heals a pruned/expired coach subscription silently on every launch. The app-icon badge is
// owned by the bell (single canonical unread), so this component only handles push registration.
export default function CoachNotificationOnboarding() {
  const [configured, setConfigured] = useState(null); // null=checking, true=ok, false=needs setup
  const [snoozed, setSnoozed] = useState(() => sessionStorage.getItem('stp_coach_push_snooze') === '1');
  const [perm, setPerm] = useState(() => (isPushSupported() ? pushPermission() : 'unsupported'));

  const checkHealth = useCallback(async () => {
    if (!isPushSupported()) { setConfigured(null); return; }
    try {
      // When permission is already granted, reconcile the browser subscription with the backend on
      // EVERY launch — this repairs a missing OR stale/key-mismatched registration so the coach is
      // never asked to re-enable after a redeploy or a service-worker update.
      if (pushPermission() === 'granted') {
        try { await enablePush(api, '/push/subscribe'); }
        catch { /* transient/blocked — the status check below decides whether to show the banner */ }
      }
      const { data: st } = await api.get('/push/status');
      setPerm(pushPermission());
      setConfigured(pushPermission() === 'granted' && (st.subscriptions || 0) > 0);
    } catch { setConfigured(false); }
  }, []);

  useEffect(() => {
    checkHealth();
    const onVisible = () => { if (document.visibilityState === 'visible') checkHealth(); };
    window.addEventListener('focus', checkHealth);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', checkHealth);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkHealth]);

  const enable = async () => {
    try {
      await enablePush(api, '/push/subscribe');
      const { data: st } = await api.get('/push/status');
      const ok = pushPermission() === 'granted' && (st.subscriptions || 0) > 0;
      setPerm(pushPermission());
      if (ok) {
        api.post('/push/test').catch(() => {}); // confirmation push completes verification on-device
        setConfigured(true);
        toast.success('Notifications on for this device — you’ll get a push for every alert.');
      } else {
        setConfigured(false);
        toast.error('We couldn’t confirm notifications just now. Please try again in a moment.');
      }
    } catch (e) {
      setPerm(pushPermission());
      const code = e?.message;
      // Only say "device doesn't support" when Web Push genuinely can't run here — never mask a
      // denied/config/transient failure as device-incompatibility.
      if (code === 'denied') toast.error('Notifications are blocked. Enable them in your browser/site settings, then try again.');
      else if (code === 'unsupported') toast.error('This device or browser doesn’t support push notifications.');
      else if (code === 'no-key') toast.error('Push isn’t configured on the server yet — please contact support.');
      else toast.error('Couldn’t set up notifications right now. Please try again.');
      setConfigured(false);
    }
  };
  const dismiss = () => { sessionStorage.setItem('stp_coach_push_snooze', '1'); setSnoozed(true); };

  // Show only when this device isn't set up, not snoozed, and the platform can plausibly support push
  // (or iOS Safari that must "Add to Home Screen" first).
  const iosNeedsInstall = isIOS() && !isStandalone();
  if (configured !== false || snoozed || !(isPushSupported() || iosNeedsInstall)) return null;

  const denied = perm === 'denied';
  return (
    <div data-testid="coach-notif-onboarding" className="mb-4 rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/[0.08] p-4 flex items-start gap-3">
      <span className="mt-0.5 w-9 h-9 rounded-full bg-[#FFD700] text-neutral-950 flex items-center justify-center shrink-0"><Bell className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">Turn on Coach OS notifications</p>
        {iosNeedsInstall ? (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">On iPhone/iPad, first tap Share → <b>Add to Home Screen</b>, open Coach OS from the home-screen icon, then enable notifications.</p>
        ) : denied ? (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Notifications are blocked for this site. Enable them in your browser/site settings (Notifications → Allow), then reopen Coach OS.</p>
        ) : (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Get a push on this device — and an unread count on the app icon — for new messages, check-ins, completed workouts and alerts.</p>
        )}
        {!iosNeedsInstall && !denied && (
          <button data-testid="coach-enable-notifications" onClick={enable}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[#FFD700] px-3.5 py-1.5 text-xs font-semibold text-neutral-950 hover:brightness-95 transition">
            <Bell className="w-3.5 h-3.5" /> Enable notifications
          </button>
        )}
      </div>
      <button data-testid="coach-notif-onboarding-dismiss" onClick={dismiss} className="p-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-black/5 shrink-0" aria-label="Dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}
